import { useEffect, useState } from 'react';
import {
  ArrowDownTrayIcon,
  TrashIcon,
  CircleStackIcon,
  ServerStackIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Header } from '@/components/Layout';
import { PageLoader, EmptyState, ConfirmDialog } from '@/components/common';
import { backupsApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import type { SystemBackup, BackupStats } from '@/types';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDate(date: string): string {
  return new Date(date).toLocaleString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function BackupsPage() {
  const [backups, setBackups] = useState<SystemBackup[]>([]);
  const [stats, setStats] = useState<BackupStats | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 20;
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  // Borrar backups es solo de super_admin; el Equipo técnico VIDA crea y descarga (VIDA-347).
  const canDelete = useAuthStore((state) => state.hasPermission('*'));
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, [page]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [backupsRes, statsRes] = await Promise.all([
        backupsApi.list(page, limit).catch(() => ({ data: { backups: [], total: 0 } })),
        backupsApi.stats().catch(() => ({ data: null })),
      ]);
      setBackups(backupsRes.data.backups || []);
      setTotal(backupsRes.data.total || 0);
      setStats(statsRes.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al cargar backups';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async (type: 'full' | 'mongodb') => {
    try {
      setCreating(true);
      await backupsApi.create(type);
      toast.success('Backup creado correctamente');
      await loadData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al crear backup';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (filename: string) => {
    try {
      const blob = await backupsApi.download(filename);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Descarga iniciada');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al descargar backup';
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setDeleting(true);
      await backupsApi.delete(deleteConfirm);
      toast.success('Backup eliminado');
      setDeleteConfirm(null);
      await loadData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al eliminar backup';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div>
      <Header
        title="Gestion de Backups"
        subtitle="Administra los backups del sistema"
        actions={
          <button onClick={loadData} className="btn-secondary" disabled={loading}>
            <ArrowPathIcon className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        }
      />

      <div className="p-6 space-y-6">
        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <ExclamationCircleIcon className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-red-900">Error</h3>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Stats cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Backups</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_backups || backups.length}</p>
              </div>
              <div className="p-3 bg-vida-100 rounded-xl">
                <DocumentDuplicateIcon className="w-6 h-6 text-vida-600" />
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Espacio Total</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatBytes(stats?.total_size || backups.reduce((acc, b) => acc + b.size_bytes, 0))}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <ServerStackIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">MongoDB</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.by_type?.mongodb || backups.filter(b => b.type === 'mongodb').length}
                </p>
              </div>
              <div className="p-3 bg-amber-100 rounded-xl">
                <CircleStackIcon className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.by_type?.full || backups.filter(b => b.type === 'full').length}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-xl">
                <ServerStackIcon className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Create backup section */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Crear Nuevo Backup</h2>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => handleCreateBackup('mongodb')}
              disabled={creating}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
            >
              <CircleStackIcon className="w-5 h-5" />
              MongoDB
            </button>

            <button
              onClick={() => handleCreateBackup('full')}
              disabled={creating}
              className="flex items-center gap-2 px-4 py-2 bg-vida-600 text-white rounded-lg hover:bg-vida-700 disabled:opacity-50 transition-colors"
            >
              <ServerStackIcon className="w-5 h-5" />
              Backup Completo
            </button>

            {creating && (
              <span className="flex items-center text-gray-500">
                <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
                Creando backup...
              </span>
            )}
          </div>
        </div>

        {/* Backups list */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Backups Disponibles</h2>
          </div>

          {backups.length === 0 ? (
            <EmptyState
              icon={CircleStackIcon}
              title="No hay backups"
              description="Crea tu primer backup usando los botones de arriba"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Archivo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tamano
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {backups.map((backup) => (
                    <tr key={backup.filename} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <CircleStackIcon className="w-5 h-5 text-gray-400 mr-3" />
                          <span className="text-sm font-medium text-gray-900">
                            {backup.filename}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            backup.type === 'mongodb'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-vida-100 text-vida-800'
                          }`}
                        >
                          {backup.type?.toUpperCase() || 'FULL'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatBytes(backup.size_bytes)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(backup.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleDownload(backup.filename)}
                          className="p-2 text-gray-400 hover:text-vida-600 transition-colors"
                          title="Descargar"
                        >
                          <ArrowDownTrayIcon className="w-5 h-5" />
                        </button>
                        {canDelete && (
                          <button
                            onClick={() => setDeleteConfirm(backup.filename)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors ml-2"
                            title="Eliminar"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {total > limit && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Mostrando {(page - 1) * limit + 1} - {Math.min(page * limit, total)} de {total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-secondary text-sm"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page * limit >= total}
                  className="btn-secondary text-sm"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Info section */}
        <div className="bg-vida-50 border border-vida-200 rounded-lg p-4">
          <h3 className="font-semibold text-vida-900 mb-2">Informacion sobre Backups</h3>
          <ul className="text-sm text-vida-800 space-y-1">
            <li>Los backups se crean automaticamente cada dia a las 03:00</li>
            <li>MongoDB: Backup de todas las bases de datos de organizaciones</li>
            <li>Backup Completo: Incluye MongoDB y configuraciones del sistema</li>
            <li>Los backups antiguos se eliminan automaticamente despues de {stats?.retention_days || 30} dias</li>
          </ul>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        title="Eliminar backup"
        message={`Estas seguro de eliminar el backup "${deleteConfirm}"? Esta accion no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
        loading={deleting}
      />
    </div>
  );
}
